<template>
  <div
    class="bet-board-actions"
    :class="{
      'bet-board-actions--fancy': fancy,
      'bet-board-actions--pin-bottom': fancy && pinBottom,
    }"
  >
    <button
      type="button"
      class="bet-board-actions__undo"
      :disabled="!canUndo"
      @mousedown="onUndoPressStart('mousedown')"
      @mouseup="onUndoPressEnd('mouseup')"
      @mouseleave="onUndoPressCancel"
      @touchstart.prevent="onUndoPressStart('touchstart')"
      @touchend="onUndoPressEnd('touchend')"
      @touchcancel="onUndoPressCancel"
    >
      <img src="@/assets/svg/btns/undo.svg" alt="" />
      <small>{{ $t('Undo') }}</small>
    </button>

    <BetChipSelector
      class="bet-board-actions__chips"
      :chips="chips"
      :active-chip="activeChip"
      :chip-colors="chipColors"
      :fancy="fancy"
      :custom="custom"
      :arena-id="arenaId"
      @select-chip="$emit('select-chip', $event)"
    />

    <button
      v-if="!fancy"
      type="button"
      class="bet-board-actions__clear"
      :disabled="!hasBets"
      @click="$emit('clear')"
    >
      {{ $t('Clear') }}
    </button>

    <!-- <div v-if="!fancy" class="bet-board-actions__stake">
      <span>STAKE</span>
      <strong>{{ currencySymbol }} {{ formatAmount(totalBet) }}</strong>
    </div> -->

    <!-- <span v-if="fancy" class="bet-board-actions__divider" aria-hidden="true"></span> -->

    <button
      type="button"
      class="bet-board-actions__submit"
      :disabled="isSubmitting || isRolling || bettingDisabled || !hasBets"
      @click="$emit('submit')"
    >
      {{ isRolling ? $t('Rolling') : $t('Bet') }}
    </button>
  </div>
</template>

<script>
import BetChipSelector from '@/components/tai-xiu/bet-chip-selector.vue';
import { formatAmount } from '@/utils/helpers';
import { isMobile } from 'mobile-device-detect';

const UNDO_HOLD_MS = 500;

export default {
  components: {
    BetChipSelector,
  },
  props: {
    chips: {
      type: Array,
      required: true,
    },
    activeChip: {
      type: Number,
      required: true,
    },
    chipColors: {
      type: Array,
      default: () => ['blue', 'orange', 'black', 'purple', 'yellow'],
    },
    canUndo: {
      type: Boolean,
      default: false,
    },
    hasBets: {
      type: Boolean,
      default: false,
    },
    totalBet: {
      type: Number,
      default: 0,
    },
    currencySymbol: {
      type: String,
      default: '',
    },
    isSubmitting: {
      type: Boolean,
      default: false,
    },
    isRolling: {
      type: Boolean,
      default: false,
    },
    bettingDisabled: {
      type: Boolean,
      default: false,
    },
    fancy: {
      type: Boolean,
      default: true,
    },
    custom: {
      type: Boolean,
      default: true,
    },
    // The table's arena, for the custom chip's per-arena save.
    arenaId: {
      type: String,
      default: '',
    },
    pinBottom: {
      type: Boolean,
      default: true,
    },
  },
  data() {
    return {
      formatAmount,
      undoHoldTimer: null,
      undoHoldClearTriggered: false,
    };
  },
  beforeDestroy() {
    this.onUndoPressCancel();
  },
  methods: {
    onUndoPressStart(trigger) {
      if (isMobile && trigger === 'mousedown') return;
      if (!this.canUndo) return;
      this.onUndoPressCancel();
      this.undoHoldClearTriggered = false;
      this.undoHoldTimer = window.setTimeout(() => {
        this.undoHoldClearTriggered = true;
        this.undoHoldTimer = null;
        this.$emit('clear');
      }, UNDO_HOLD_MS);
    },
    onUndoPressEnd(trigger) {
      if (isMobile && trigger === 'mouseup') return;
      if (this.undoHoldTimer) {
        clearTimeout(this.undoHoldTimer);
        this.undoHoldTimer = null;
        if (!this.undoHoldClearTriggered && this.canUndo) {
          this.$emit('undo');
        }
      }
      this.undoHoldClearTriggered = false;
    },
    onUndoPressCancel() {
      if (this.undoHoldTimer) {
        clearTimeout(this.undoHoldTimer);
        this.undoHoldTimer = null;
      }
      this.undoHoldClearTriggered = false;
    },
  },
};
</script>

<style lang="scss" scoped>
.bet-board-actions {
  display: flex;
  align-items: center;
  gap: 6px;

  button {
    min-height: 34px;
    border-radius: 8px;
    cursor: pointer;

    &:disabled {
      opacity: 0.4;
      cursor: default;
    }
  }
}

.bet-board-actions__clear {
  border: 1px solid #35465d;
  color: #a9b6c9;
  background: #101f33;
  padding: 0 10px;
}

.bet-board-actions__submit {
  flex: 1;
  border: 1px solid #d68c31;
  color: #fff;
  background: linear-gradient(180deg, #9d5b20, #613514);
  font-weight: 800;
}

.bet-board-actions__stake {
  min-width: 48px;
  max-width: 92px;
  overflow: hidden;
  color: #d5def0;

  span,
  strong {
    display: block;
  }

  span {
    font-size: 9px;
    font-weight: 700;
    line-height: 1;
    text-transform: uppercase;
    color: #ACBFF2;
  }

  strong {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.1;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }
}

.bet-board-actions--fancy {
  display: grid;
  // Undo | chips | Bet. Restore a 1px track between the last two if the
  // divider ever comes back, otherwise Bet lands in it and overlaps the chips.
  grid-template-columns: 40px minmax(0, 1fr) 100px;
  column-gap: 12px;
  row-gap: 8px;
  align-items: center;
  overflow: visible;
  // The custom chip's amount field spans this row and anchors to it.
  position: relative;

  .bet-board-actions__undo {
    width: 40px;
    height: 40px;
    padding: 0;
    border: 0;
    border-radius: 0;
    color: #acbff2;
    background: transparent;
    display: flex;
    flex-direction: column;
    align-items: center;

    img,
    small {
      display: block;
    }

    img {
      width: 36px;
      height: 36px;
    }

    small {
      width: 40px;
      height: 13px;
      margin-top: -9px;
      border-radius: 4px;
      background: #324a8d;
      font-size: 10px;
      font-weight: 700;
      line-height: 13px;
      text-transform: uppercase;
      position: relative;
    }
  }

  .bet-board-actions__chips {
    display: flex;
    align-items: center;
    // Spread the chips across the whole track between Undo and Bet. The gap is
    // only a floor, so they stay apart when the track is narrow.
    justify-content: space-between;
    width: 100%;
    min-width: 0;
    min-height: 52px;
    padding: 8px 0;
    box-sizing: border-box;
    gap: 4px;
  }

  .bet-board-actions__submit {
    // Fills its track: an 80px track held a 100px button, which overflowed
    // 20px back over the chips.
    width: 100%;
    justify-self: end;
    min-height: 36px;
    height: 36px;
    padding: 0;
    border: 0;
    border-radius: 0;
    color: #59401a;
    background: url('~@/assets/images/bet-btn-tx.webp') center/100% 100% no-repeat;
    box-shadow: none;
    font-size: 18px;
    font-weight: 800;
    line-height: 1;
    text-transform: uppercase;
  }
}

/*
 * The custom chip's amount field is cockfight's own (custom-chip.vue): a sheet
 * over the row, Maximum | input, then the chip and a plain "Save" on the
 * right. Desktop keeps it as is (its gutter is set in tai-xiu-pane.vue); a
 * phone row is far narrower, so it is fitted to it:
 *  - BET is hidden while the field is open and the sheet is opaque and on
 *    top, so BET's gold art never shows behind "Save";
 *  - "Save" is text centred in BET's own 100px track;
 *  - the input stops well short of the chip, which sits just before that
 *    track (100 + 12 gap + 36px chip + 24 clearance = 172);
 *  - tighter insets and type, so the label and input fit ~360px.
 */
@media (max-width: 767px) {
  .bet-board-actions--fancy:has(.custom-chip-input) .bet-board-actions__submit {
    visibility: hidden;
  }

  // The chip stays in view beside the input, above the sheet (20).
  .bet-board-actions--fancy ::v-deep .custom-chip {
    z-index: 21;
  }

  .bet-board-actions--fancy ::v-deep .custom-chip-input {
    top: 0;
    height: 100%;
    padding: 0 172px 0 12px;
    gap: 8px;
    border-radius: 8px;
    background-color: #000;
    opacity: 1;
    z-index: 20;
    font-size: 11px;

    .label {
      flex: 0 0 auto;
      height: auto;
      margin: 0;
      line-height: 1.25;
    }

    .input {
      flex: 1 1 auto;
      min-width: 0;
      max-width: none;
      padding: 0;

      input {
        display: block;
        height: 32px;
        font-size: 17px;
        border-radius: 16px;
      }
    }

    .label-right {
      right: 0;
      width: 100px;
      text-align: center;
      font-size: 15px;
      font-weight: 600;
    }
  }
}

.bet-board-actions--pin-bottom {
  margin-top: auto;
  // padding-top: 16px;
}
</style>
